import React, { useState, useEffect, useContext, useRef } from 'react';
import { Box, Slide, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Beer } from '../../state/types';
import { useBeer } from '../../state/BeerContext';
import { subscribeToSelection, clearGlobalSelection } from '../BeerCard/BeerCardContainer';
import { OnboardingTourContext, ONBOARDING_STEP_INDEXES } from '../../state/hooks/useOnboardingTour';
import {
  buildBeerFavouritedEvent,
  buildBeerTastingListAddedEvent,
  buildBeerTastingListRemovedEvent,
  buildBeerUnfavouritedEvent,
  useAnalyticsService
} from '../../services/analytics';
import styles from './SelectionActionBar.module.css';
import {
  partitionBeersByTastingList,
  partitionBeersByFavourites,
  applyVisualFeedbackToCards,
  buildActionMessage
} from './selectionHelpers';
import { ActionButtons } from './ActionButtons';
import { BrewletteOnlyBar } from './BrewletteOnlyBar';

/**
 * SelectionActionBar component - displays contextual actions for a selected beer
 * Uses direct subscription to selection state for faster updates
 */
interface SelectionActionBarProps {
  showBrewlette?: boolean;
  onBrewletteClick?: () => void;
}

const SelectionActionBar: React.FC<SelectionActionBarProps> = ({
  showBrewlette = false,
  onBrewletteClick
}) => {
  const [selectedBeerIds, setSelectedBeerIds] = useState<Set<string>>(new Set());
  const [showNotificationState, setShowNotificationState] = useState(false);
  const [currentMessage, setCurrentMessage] = useState('');
  const notificationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  const { 
    state: { beers },
    toggleTastingList,
    toggleFavorite,
    userInteractions: { tastingList, favorites } 
  } = useBeer();
  const analyticsService = useAnalyticsService();
  

  // Subscribe to selection changes directly (bypasses context)
  useEffect(() => {
    const unsubscribe = subscribeToSelection((ids) => {
      setSelectedBeerIds(ids);
    });
    return () => { unsubscribe(); };
  }, [beers.length]);

  const showNotificationMessage = (message: string) => {
    setCurrentMessage(message);
    setShowNotificationState(true);

    if (notificationTimerRef.current) {
      clearTimeout(notificationTimerRef.current);
    }

    notificationTimerRef.current = setTimeout(() => {
      setShowNotificationState(false);
      setCurrentMessage('');
    }, 1500);
  };

  useEffect(() => {
    return () => {
      if (notificationTimerRef.current) {
        clearTimeout(notificationTimerRef.current);
      }
    };
  }, []);

  const selectedBeers: Beer[] = Array.from(selectedBeerIds)
    .map(id => beers.find(b => b.id === id))
    .filter((b): b is Beer => b !== undefined);

  const handleClearSelection = () => {
    clearGlobalSelection();
  };

  const tourContext = useContext(OnboardingTourContext);
  const showForOnboarding = tourContext?.isOnboardingActive
    && tourContext?.currentStep === ONBOARDING_STEP_INDEXES.selectionActions;
  
  const visibility = calculateVisibility(
    selectedBeers.length,
    showForOnboarding ?? false,
    showNotificationState,
    currentMessage.length > 0,
    showBrewlette ?? false
  );
  
  if (!visibility.shouldShow) {
    return null;
  }

  if (visibility.isBrewletteOnly && onBrewletteClick) {
    return (
      <Slide direction="up" in={visibility.shouldShow} mountOnEnter unmountOnExit>
        <BrewletteOnlyBar onBrewletteClick={onBrewletteClick} />
      </Slide>
    );
  }

  const listPartition = partitionBeersByTastingList(selectedBeers, tastingList, showForOnboarding ?? false);
  const favouritePartition = partitionBeersByFavourites(selectedBeers, favorites, showForOnboarding ?? false);

  const handleTastingListClick = () => {
    if (selectedBeers.length === 0) return;
    
    const beersToAct = listPartition.shouldAdd ? listPartition.beersNotInList : listPartition.beersInList;
    if (beersToAct.length === 0) return;

    trackTastingListEvents(beersToAct, listPartition.shouldAdd, analyticsService);
    applyVisualFeedbackToCards(beersToAct, listPartition.shouldAdd);
    
    const message = buildActionMessage(beersToAct.length, listPartition.shouldAdd, 'list');
    showNotificationMessage(message);
    
    setTimeout(() => handleClearSelection(), 600);
    setTimeout(() => {
      beersToAct.forEach(beer => toggleTastingList(beer.id));
    }, 0);
  };
  
  const handleFavouriteClick = () => {
    if (selectedBeers.length === 0) return;
    
    const beersToAct = favouritePartition.shouldFavourite
      ? favouritePartition.beersNotFavourited
      : favouritePartition.beersFavourited;
    if (beersToAct.length === 0) return;

    trackFavouriteEvents(beersToAct, favouritePartition.shouldFavourite, analyticsService);
    
    const message = buildActionMessage(beersToAct.length, favouritePartition.shouldFavourite, 'favourite');
    showNotificationMessage(message);
    
    setTimeout(() => handleClearSelection(), 600);
    setTimeout(() => {
      beersToAct.forEach(beer => toggleFavorite(beer.id));
    }, 0);
  };

  const ActionBarContent = React.forwardRef<HTMLDivElement>((props, ref) => (
    <Box 
      ref={ref}
      role="region"
      aria-label="Beer selection actions"
      className={`${styles.actionBar} ${styles.actionBarCompact}`}
      data-tour="selection-action-bar"
      sx={{
        bottom: '72px',
        paddingLeft: '16px',
        paddingRight: '16px',
      }}
    >
        <Box className={`${styles.content} ${styles.contentCompact}`}>
          {visibility.showClearButton && (
            <IconButton
              onClick={handleClearSelection}
              className={`${styles.closeButton} ${styles.closeButtonCompact}`}
              aria-label="Clear selection"
              sx={{
                backgroundColor: 'rgba(0, 0, 0, 0.72)',
                color: 'var(--primary-colour)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.88)',
                  color: 'var(--primary-colour)',
                }
              }}
            >
              <CloseIcon sx={{ fontSize: '1.65rem' }} />
            </IconButton>
          )}

          {visibility.showActionButtons && (
            <Box className={`${styles.actions} ${showNotificationState ? styles.actionsNotification : ''} ${showBrewlette ? styles.actionsThree : ''} ${styles.actionsCompact}`}>
              <ActionButtons
                showNotificationState={showNotificationState}
                currentMessage={currentMessage}
                shouldShowSelectionButtons={visibility.shouldShowSelectionButtons}
                showBrewlette={showBrewlette}
                listPartition={listPartition}
                favouritePartition={favouritePartition}
                onTastingListClick={handleTastingListClick}
                onFavouriteClick={handleFavouriteClick}
                onBrewletteClick={onBrewletteClick}
              />
            </Box>
          )}
        </Box>
    </Box>
  ));

  return (
    <Slide direction="up" in={visibility.shouldShow} mountOnEnter unmountOnExit>
      <ActionBarContent />
    </Slide>
  );
};

interface VisibilityState {
  shouldShow: boolean
  showActionButtons: boolean
  showClearButton: boolean
  shouldShowSelectionButtons: boolean
  isBrewletteOnly: boolean
}

function calculateVisibility(
  selectedCount: number,
  showForOnboarding: boolean,
  showNotificationState: boolean,
  hasMessage: boolean,
  showBrewlette: boolean
): VisibilityState {
  const hasSelection = selectedCount > 0
  const shouldShow = hasSelection || showForOnboarding || showNotificationState || hasMessage || showBrewlette
  const showActionButtons = hasSelection || showNotificationState || showBrewlette || showForOnboarding
  const showClearButton = hasSelection || showNotificationState || showForOnboarding
  const shouldShowSelectionButtons = hasSelection || showForOnboarding || showNotificationState
  const isBrewletteOnly = showBrewlette && !shouldShowSelectionButtons && !showNotificationState

  return {
    shouldShow,
    showActionButtons,
    showClearButton,
    shouldShowSelectionButtons,
    isBrewletteOnly
  }
}

function trackTastingListEvents(
  beers: Beer[],
  shouldAdd: boolean,
  analyticsService: ReturnType<typeof useAnalyticsService>
): void {
  beers.forEach((beer) => {
    const event = shouldAdd
      ? buildBeerTastingListAddedEvent(beer)
      : buildBeerTastingListRemovedEvent(beer)
    analyticsService.trackEvent(event)
  })
}

function trackFavouriteEvents(
  beers: Beer[],
  shouldFavourite: boolean,
  analyticsService: ReturnType<typeof useAnalyticsService>
): void {
  beers.forEach((beer) => {
    const event = shouldFavourite
      ? buildBeerFavouritedEvent(beer)
      : buildBeerUnfavouritedEvent(beer)
    analyticsService.trackEvent(event)
  })
}

export default SelectionActionBar;
